import { useState, useMemo, useEffect, useCallback, useRef } from 'react';

interface UsePaginationOptions<T> {
  data: T[];
  pageSize: number;
  resetOn?: unknown[];
}

interface UsePaginationResult<T> {
  paginatedData: T[];
  currentPage: number;
  totalPages: number;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  goToPage: (page: number) => void;
  resetPage: () => void;
}

export function usePagination<T>({
  data,
  pageSize,
  resetOn = [],
}: UsePaginationOptions<T>): UsePaginationResult<T> {
  const [currentPage, setCurrentPage] = useState(0);
  const isFirstRenderRef = useRef(true);

  const totalPages = Math.ceil(data.length / pageSize);

  const prevResetOnRef = useRef<unknown[]>(resetOn);

  useEffect(() => {
    const hasResetOnChanged =
      prevResetOnRef.current.length !== resetOn.length ||
      resetOn.some((val, i) => val !== prevResetOnRef.current[i]);

    prevResetOnRef.current = resetOn;

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (hasResetOnChanged) {
      setCurrentPage(0);
    } else if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [resetOn, currentPage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, currentPage, pageSize]);

  const goToPrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(0, p - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
  }, [totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)));
    },
    [totalPages]
  );

  const resetPage = useCallback(() => {
    setCurrentPage(0);
  }, []);

  return {
    paginatedData,
    currentPage,
    totalPages,
    goToPrevPage,
    goToNextPage,
    goToPage,
    resetPage,
  };
}
